import { useState, useCallback, useRef } from 'react';
import type { UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { client } from '../../services/api';
import { isAbortError } from '../../utils/abortableRequest';
import type { AddressValidationResult, CreatePatientFormData } from './types';

interface UseAddressValidationOptions {
  setValue: UseFormSetValue<CreatePatientFormData>;
  getValues: UseFormGetValues<CreatePatientFormData>;
  saveToServer: (updates: Partial<CreatePatientFormData>) => Promise<void>;
}

interface UseAddressValidationReturn {
  addressSuggestion: AddressValidationResult | null;
  isValidatingAddress: boolean;
  addressModalOpen: boolean;
  setAddressModalOpen: (open: boolean) => void;
  checkAddressValidation: (
    streetAddress: string,
    zipCode: string
  ) => Promise<void>;
  handleAcceptAddressSuggestion: () => void;
  resetValidation: () => void;
}

/**
 * Hook for validating addresses via Roger Doc API.
 * Manages address suggestion modal state and handles API calls.
 */
export function useAddressValidation({
  setValue,
  getValues,
  saveToServer,
}: UseAddressValidationOptions): UseAddressValidationReturn {
  const [addressSuggestion, setAddressSuggestion] =
    useState<AddressValidationResult | null>(null);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const checkAddressValidation = useCallback(
    async (streetAddress: string, zipCode: string) => {
      if (!streetAddress.trim() || !zipCode.trim()) return;

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const currentController = new AbortController();
      abortControllerRef.current = currentController;

      try {
        setIsValidatingAddress(true);
        const encodedStreet = encodeURIComponent(streetAddress.trim());
        const encodedZip = encodeURIComponent(zipCode.trim());

        const response = await client.request({
          method: 'GET',
          url: `/rogerdoc/api/patient/address/validate/${encodedStreet}/${encodedZip}`,
          signal: currentController.signal,
        });

        const result = response.data as AddressValidationResult;
        if (result && result.street) {
          setAddressSuggestion(result);
          setAddressModalOpen(true);
        }
      } catch (error: unknown) {
        if (!isAbortError(error)) {
          console.error('Address validation error:', error);
        }
      } finally {
        // Only set loading to false if this is still the current request
        if (abortControllerRef.current === currentController) {
          setIsValidatingAddress(false);
        }
      }
    },
    []
  );

  const resetValidation = useCallback(() => {
    setAddressSuggestion(null);
    setAddressModalOpen(false);
    setIsValidatingAddress(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const handleAcceptAddressSuggestion = useCallback(() => {
    if (!addressSuggestion) return;

    setValue('patient_street_address', addressSuggestion.street || '');
    setValue('patient_city', addressSuggestion.city || '');
    setValue('patient_state', addressSuggestion.state || '');
    setValue('patient_zip_code', addressSuggestion.zip_code || '');
    setValue('patient_county', addressSuggestion.county || '');

    setAddressModalOpen(false);
    setAddressSuggestion(null);

    // Save the updated values
    setTimeout(() => {
      const allValues = getValues();
      saveToServer(allValues);
    }, 0);
  }, [addressSuggestion, setValue, getValues, saveToServer]);

  return {
    addressSuggestion,
    isValidatingAddress,
    addressModalOpen,
    setAddressModalOpen,
    checkAddressValidation,
    handleAcceptAddressSuggestion,
    resetValidation,
  };
}
